import { styled } from "goober";
import { useEffect, useState } from "react";
import {
  desktopViewportFrameCss,
  useDesktopViewportScale,
} from "../hooks/useDesktopViewport";
import { executionContentPath } from "../lib/apiPaths";
import { apiUrl } from "../lib/tenantOrigin";
import { HtmlViewer } from "./HtmlViewer";
import { WavyLine } from "./WavyLine";

// Cache fetched output HTML by executionId so windowing teardown/remount and
// leaving and returning to the page don't refetch and reparse. Ids are
// content-addressed (a new render yields a new id), so an entry is never stale.
// Bounded so scrolling a large gallery can't retain every document for the tab's
// lifetime; the oldest entries evict first (insertion order).
const HTML_CACHE_MAX = 150;
const htmlCache = new Map<string, string>();

function cacheHtml(executionId: string, html: string) {
  htmlCache.set(executionId, html);
  for (const oldest of htmlCache.keys()) {
    if (htmlCache.size <= HTML_CACHE_MAX) break;
    htmlCache.delete(oldest);
  }
}

const Wrapper = styled("div")`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-primary);
  overflow: hidden;
`;

// White matches a normal browser canvas for pages that don't set body bg, so
// the rendered iframe area looks the same as opening the page in a tab.
const Thumbnail = styled(HtmlViewer)`
  ${desktopViewportFrameCss}
  position: absolute;
  top: 0;
  left: 0;
  border: none;
  display: block;
  background: white;
  pointer-events: none;
`;

type Props = {
  executionId: string;
  alt: string;
  baseRoute?: string;
};

export function PageThumbnail({ executionId, alt, baseRoute }: Props) {
  const externalApiUrl = apiUrl();
  const wrapperRef = useDesktopViewportScale<HTMLDivElement>();

  const [html, setHtml] = useState<string | null>(
    () => htmlCache.get(executionId) ?? null
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    const cached = htmlCache.get(executionId);
    if (cached !== undefined) {
      setHtml(cached);
      return;
    }
    setHtml(null);
    const ac = new AbortController();
    fetch(
      `${externalApiUrl}${executionContentPath(executionId, "output", { body: true })}`,
      { credentials: "include", signal: ac.signal }
    )
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((text) => {
        cacheHtml(executionId, text);
        setHtml(text);
      })
      .catch((e) => {
        if (e?.name !== "AbortError") setFailed(true);
      });
    return () => ac.abort();
  }, [executionId, externalApiUrl]);

  if (failed || !html) {
    return (
      <Wrapper ref={wrapperRef} aria-label={alt}>
        <WavyLine />
      </Wrapper>
    );
  }

  return (
    <Wrapper ref={wrapperRef} aria-label={alt}>
      <Thumbnail html={html} title={alt} baseRoute={baseRoute} />
    </Wrapper>
  );
}
